import { useState, useEffect, useCallback, useRef } from "react";
import {
  PaperPlaneTilt, ChatCircle, Spinner, ArrowClockwise, DotsThreeVertical,
  Phone, MagnifyingGlass, Smiley, Paperclip, Microphone, Check, Checks, ArrowLeft,
  LinkSimple, Moon, Archive, Trash, ArrowCounterClockwise, EnvelopeOpen, Prohibit,
} from "@phosphor-icons/react";
import api, {
  sendSms, fetchSmsThread, fetchSmsThreadByPhone, refreshSmsStatus, twilioHealth,
} from "@/lib/api";
import LinkOrphanDialog from "@/components/LinkOrphanDialog";

/**
 * SmsChatView — full-pane SMS thread, styled like WhatsApp Web.
 *
 * Renders inside a flex container (no portal, no overlay). Caller controls
 * which thread is active via the `customerId` / `personId` props. Handles its
 * own polling, status refresh, AI suggestions, and send.
 *
 * Compared to the legacy SmsChatPanel:
 *   • Inline (no modal/portal)
 *   • WhatsApp-Web color palette (green #00a884 header, #efeae2 chat bg with
 *     the ribbon-pattern SVG, #d9fdd3 outgoing bubbles, white incoming bubbles)
 *   • Single/double tick delivery icons next to timestamps
 *   • Empty-state placeholder when no thread is selected
 */
export default function SmsChatView({
  customerId,
  customerName = "",
  personId,
  personName = "",
  personPhone = "",
  archived = false,
  onBack, // mobile-only back button (hides on >= md)
  onAfterSend,
  onLinked, // called when a phone-only thread gets linked to a person
  onArchive, // optional: called from the header overflow menu
  onSnooze,
  onUnarchive,
  onDelete,
  onMarkUnread,
  onBlock,
  onUnblock,
  emptyHint = "Select a conversation to start chatting.",
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [smsFrom, setSmsFrom] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const scrollRef = useRef(null);
  const menuRef = useRef(null);

  // Close the overflow menu on outside click (no portal — simple ref check).
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (ev) => {
      if (menuRef.current && !menuRef.current.contains(ev.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  // Effective key the chat view operates on. We support 3 modes:
  //   • known + phone:    customerId set AND personPhone set → filter the
  //     customer's thread to messages exchanged with that specific number,
  //     so main vs alt phones are NEVER merged.
  //   • known no phone:   legacy customer-only thread (all phones merged).
  //   • orphan phone:     no customerId, only a phone → fetch via the
  //     /sms/threads-by-phone endpoint and reply with `to=phone`.
  const phoneKeyed = !customerId && !!personPhone;
  const hasThread = !!customerId || phoneKeyed;

  const refresh = useCallback(async () => {
    if (!hasThread) return;
    setLoading(true);
    try {
      try { if (customerId) await refreshSmsStatus(customerId); } catch { /* non-fatal */ }
      let rows;
      if (customerId) {
        const r = await fetchSmsThread(customerId, 200, personPhone || null);
        rows = r.items || [];
      } else {
        const r = await fetchSmsThreadByPhone(personPhone);
        rows = r.items || [];
      }
      setItems(rows || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not load SMS thread");
    } finally {
      setLoading(false);
    }
  }, [customerId, personPhone, hasThread]);

  // Reset state when the active thread changes.
  useEffect(() => {
    setItems([]);
    setBody("");
    setError(null);
    setSuggestions([]);
    if (hasThread) refresh();
  }, [customerId, personId, personPhone, hasThread, refresh]);

  useEffect(() => {
    twilioHealth().then((h) => setSmsFrom(h?.sms_from || "")).catch(() => {});
  }, []);

  // AI smart-reply suggestions — only meaningful when we have a known
  // customer (the suggestions endpoint pulls customer/company context).
  // For orphan phone-only threads we skip suggestions until a person is linked.
  const lastMessageId = items[items.length - 1]?.id;
  useEffect(() => {
    if (!customerId || items.length === 0) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    setSuggesting(true);
    const params = personId ? { person_id: personId } : {};
    api.get(`/twilio/sms/threads/${encodeURIComponent(customerId)}/suggestions`, { params })
      .then((r) => { if (!cancelled) setSuggestions(r.data?.suggestions || []); })
      .catch(() => { if (!cancelled) setSuggestions([]); })
      .finally(() => { if (!cancelled) setSuggesting(false); });
    return () => { cancelled = true; };
  }, [customerId, personId, lastMessageId, items.length]);

  // Auto-poll while there are pending outbound messages waiting for delivery.
  useEffect(() => {
    if (!hasThread) return undefined;
    const TERMINAL = new Set(["delivered", "failed", "undelivered", "received", "read"]);
    const hasPending = items.some(
      (m) => m.direction === "outbound" && !TERMINAL.has((m.status || "").toLowerCase())
    );
    if (!hasPending) return undefined;
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [hasThread, items, refresh]);

  // Auto-scroll to bottom on new messages.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [items]);

  const onSend = async () => {
    const text = body.trim();
    if (!text || !hasThread) return;
    setSending(true); setError(null);
    try {
      // Three call shapes:
      //   • known + phone: customer_id + person_id + to=phone (so the
      //     outbound is stamped with the same other-side number → stays
      //     in this exact thread).
      //   • known no phone: legacy behavior (customer_id + person_id).
      //   • orphan: to=phone only; backend will reverse-lookup if the
      //     number now belongs to a person.
      const args = customerId
        ? { customer_id: customerId, person_id: personId, to: personPhone || undefined, body: text }
        : { to: personPhone, body: text };
      await sendSms(args);
      setBody("");
      await refresh();
      onAfterSend?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Send failed");
    } finally {
      setSending(false);
    }
  };

  // Empty placeholder — WhatsApp-style "Keep your phone connected" card.
  if (!hasThread) {
    return (
      <div
        data-testid="sms-chat-view-empty"
        className="flex-1 flex flex-col items-center justify-center text-center p-10"
        style={{ background: "#f0f2f5" }}
      >
        <div className="w-32 h-32 rounded-full flex items-center justify-center mb-6"
             style={{ background: "#dadde1" }}>
          <ChatCircle size={64} weight="duotone" className="text-slate-500" />
        </div>
        <h2 className="text-2xl font-light text-slate-700 tracking-tight mb-2">
          USAir Compressor SMS
        </h2>
        <p className="text-sm text-slate-500 max-w-md leading-relaxed">
          {emptyHint}
        </p>
        <div className="mt-10 text-[11px] text-slate-400 border-t border-slate-200 pt-4 w-full max-w-md">
          End-to-end via Twilio · Replies route here automatically.
        </div>
      </div>
    );
  }

  // Group messages by calendar day so we can render WhatsApp-style date dividers.
  const grouped = [];
  let lastDay = null;
  for (const m of items) {
    const d = new Date(m.created_at);
    const dayKey = d.toDateString();
    if (dayKey !== lastDay) {
      grouped.push({ kind: "divider", id: `div-${dayKey}`, label: formatDayLabel(d) });
      lastDay = dayKey;
    }
    grouped.push({ kind: "msg", ...m });
  }

  return (
    <div className="flex-1 flex flex-col min-w-0" data-testid="sms-chat-view">
      {/* Header */}
      <div
        className="h-16 px-4 flex items-center justify-between shrink-0 shadow-sm"
        style={{ background: "#f0f2f5", borderBottom: "1px solid #d1d7db" }}
      >
        <div className="flex items-center gap-3 min-w-0">
          {onBack && (
            <button
              onClick={onBack}
              data-testid="sms-chat-back"
              className="md:hidden -ml-1 w-8 h-8 flex items-center justify-center rounded-full text-slate-600 hover:bg-slate-200"
              title="Back to list"
            >
              <ArrowLeft size={18} weight="bold" />
            </button>
          )}
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white text-sm shrink-0"
            style={{ background: customerId ? "#00a884" : "#94a3b8" }}
          >
            {customerId
              ? (customerName || personName || "?").trim().charAt(0).toUpperCase()
              : "?"}
          </div>
          <div className="min-w-0">
            <div className="text-[15px] font-medium text-slate-900 truncate">
              {customerId ? (customerName || "Unknown") : "Unknown number"}
            </div>
            <div className="text-xs text-slate-500 truncate">
              {personName ? `${personName}` : ""}
              {personName && personPhone ? " · " : ""}
              {personPhone}
              {!customerId && (
                <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-sm">
                  Not linked
                </span>
              )}
              {archived ? " · archived" : ""}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 text-slate-600">
          {!customerId && phoneKeyed && (
            <button
              onClick={() => setLinkDialogOpen(true)}
              data-testid="sms-chat-link-btn"
              className="inline-flex items-center gap-1.5 h-8 px-3 text-[11px] font-bold uppercase tracking-wider text-white rounded-sm hover:opacity-90"
              style={{ background: "#00a884" }}
              title="Attach this phone number to a contact"
            >
              <LinkSimple size={11} weight="bold" />
              Link to person
            </button>
          )}
          <button
            onClick={refresh}
            disabled={loading}
            data-testid="sms-chat-refresh"
            className={`w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-200 ${loading ? "text-emerald-600" : ""}`}
            title="Refresh"
          >
            <ArrowClockwise size={18} className={loading ? "animate-spin" : ""} />
          </button>
          <button
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-200 hidden sm:flex"
            title="Search (coming soon)"
            disabled
          >
            <MagnifyingGlass size={18} />
          </button>
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="sms-chat-menu-btn"
              className={`w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-200 ${menuOpen ? "bg-slate-200" : ""}`}
              title="More actions"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <DotsThreeVertical size={20} weight="bold" />
            </button>
            {menuOpen && (
              <div
                role="menu"
                data-testid="sms-chat-menu"
                className="absolute right-0 top-10 z-30 min-w-[220px] bg-white border border-slate-200 rounded-md shadow-xl py-1 text-sm"
              >
                {onMarkUnread && items.length > 0 && (
                  <button type="button" role="menuitem"
                    onClick={() => { setMenuOpen(false); onMarkUnread(); }}
                    data-testid="sms-chat-menu-mark-unread"
                    className="w-full text-left px-4 py-2 hover:bg-emerald-50 inline-flex items-center gap-2.5">
                    <EnvelopeOpen size={14} weight="bold" className="text-emerald-600" />
                    <span>Mark as unread</span>
                  </button>
                )}
                {!archived && onSnooze && (
                  <button type="button" role="menuitem"
                    onClick={() => { setMenuOpen(false); onSnooze(); }}
                    data-testid="sms-chat-menu-snooze"
                    className="w-full text-left px-4 py-2 hover:bg-violet-50 inline-flex items-center gap-2.5">
                    <Moon size={14} weight="fill" className="text-violet-600" />
                    <span>Snooze conversation</span>
                  </button>
                )}
                {!archived && onArchive && (
                  <button type="button" role="menuitem"
                    onClick={() => { setMenuOpen(false); onArchive(); }}
                    data-testid="sms-chat-menu-archive"
                    className="w-full text-left px-4 py-2 hover:bg-amber-50 inline-flex items-center gap-2.5">
                    <Archive size={14} weight="bold" className="text-amber-600" />
                    <span>Archive conversation</span>
                  </button>
                )}
                {archived && onUnarchive && (
                  <button type="button" role="menuitem"
                    onClick={() => { setMenuOpen(false); onUnarchive(); }}
                    data-testid="sms-chat-menu-unarchive"
                    className="w-full text-left px-4 py-2 hover:bg-blue-50 inline-flex items-center gap-2.5">
                    <ArrowCounterClockwise size={14} weight="bold" className="text-blue-600" />
                    <span>Unarchive — restore to active</span>
                  </button>
                )}
                {(onBlock || onUnblock) && <div className="border-t border-slate-100 my-1" />}
                {onBlock && (
                  <button type="button" role="menuitem"
                    onClick={() => { setMenuOpen(false); onBlock(); }}
                    data-testid="sms-chat-menu-block"
                    className="w-full text-left px-4 py-2 hover:bg-rose-50 inline-flex items-center gap-2.5 text-rose-700">
                    <Prohibit size={14} weight="bold" />
                    <span>Block this number</span>
                  </button>
                )}
                {onUnblock && (
                  <button type="button" role="menuitem"
                    onClick={() => { setMenuOpen(false); onUnblock(); }}
                    data-testid="sms-chat-menu-unblock"
                    className="w-full text-left px-4 py-2 hover:bg-emerald-50 inline-flex items-center gap-2.5 text-emerald-700">
                    <Prohibit size={14} weight="bold" className="opacity-50" />
                    <span>Unblock this number</span>
                  </button>
                )}
                {(onSnooze || onArchive || onUnarchive || onMarkUnread || onBlock || onUnblock) && onDelete && (
                  <div className="border-t border-slate-100 my-1" />
                )}
                {onDelete && (
                  <button type="button" role="menuitem"
                    onClick={() => { setMenuOpen(false); onDelete(); }}
                    data-testid="sms-chat-menu-delete"
                    className="w-full text-left px-4 py-2 hover:bg-rose-50 inline-flex items-center gap-2.5 text-rose-700">
                    <Trash size={14} weight="bold" />
                    <span>Delete conversation</span>
                  </button>
                )}
                {!onSnooze && !onArchive && !onUnarchive && !onDelete && !onMarkUnread && !onBlock && !onUnblock && (
                  <div className="px-4 py-2 text-xs text-slate-400 italic">No actions available</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {linkDialogOpen && (
        <LinkOrphanDialog
          phone={personPhone}
          onClose={() => setLinkDialogOpen(false)}
          onLinked={(res) => {
            setLinkDialogOpen(false);
            onLinked?.(res);
          }}
        />
      )}

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 sm:px-12 py-3 space-y-1"
        style={{
          background: "#efeae2",
          backgroundImage: WA_PATTERN,
        }}
        data-testid="sms-chat-messages"
      >
        {!loading && items.length === 0 && (
          <div className="flex justify-center mt-6">
            <div className="text-[12px] text-slate-700 px-3 py-2 rounded-md shadow-sm"
                 style={{ background: "#fff3c4" }}>
              No messages yet — send the first one below. Replies will arrive here automatically.
            </div>
          </div>
        )}
        {grouped.map((g) => {
          if (g.kind === "divider") {
            return (
              <div key={g.id} className="flex justify-center my-3" data-testid={`sms-day-${g.id}`}>
                <span
                  className="text-[11px] font-medium text-slate-700 px-3 py-1 rounded-md shadow-sm"
                  style={{ background: "#ffffffe6" }}
                >
                  {g.label}
                </span>
              </div>
            );
          }
          const out = g.direction === "outbound";
          const failed = ["failed", "undelivered"].includes((g.status || "").toLowerCase());
          return (
            <div key={g.id} className={`flex ${out ? "justify-end" : "justify-start"}`}>
              <div
                data-testid={`sms-bubble-${g.id}`}
                className="relative max-w-[78%] sm:max-w-[65%] rounded-lg px-2.5 py-1.5 shadow-sm"
                style={{
                  background: out ? (failed ? "#fde2e2" : "#d9fdd3") : "#ffffff",
                  borderTopRightRadius: out ? 2 : 8,
                  borderTopLeftRadius: out ? 8 : 2,
                }}
              >
                <div className="whitespace-pre-wrap break-words text-[14.5px] leading-snug text-slate-900">
                  {g.body || <span className="italic text-slate-400">(empty)</span>}
                  {/* Invisible timestamp spacer — the WhatsApp-Web trick that
                      reserves inline-flow space so the absolutely-positioned
                      meta row never overlaps short message bodies. */}
                  <span className="inline-block align-bottom invisible select-none ml-2" aria-hidden="true">
                    <span className="text-[10.5px]">
                      {formatBubbleTime(g.created_at)}
                      {out ? " __" : ""}
                    </span>
                  </span>
                </div>
                <div className="absolute right-2 bottom-1 flex items-center gap-1 text-[10.5px] text-slate-500 pointer-events-none">
                  <span>{formatBubbleTime(g.created_at)}</span>
                  {out && <DeliveryTicks status={g.status} failed={failed} />}
                </div>
                {failed && g.twilio_error_message && (
                  <div className="mt-1 text-[10.5px] text-rose-700 italic"
                       data-testid={`sms-bubble-error-${g.id}`}>
                    Error {g.twilio_error_code}: {g.twilio_error_message}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      <div className="shrink-0 px-3 py-2.5"
           style={{ background: "#f0f2f5", borderTop: "1px solid #d1d7db" }}>
        {error && (
          <div className="mb-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
            {error}
          </div>
        )}
        {(suggesting || suggestions.length > 0) && (
          <div className="mb-2 flex flex-wrap items-center gap-1.5" data-testid="sms-suggestions">
            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 inline-flex items-center gap-1">
              {suggesting ? <Spinner size={10} className="animate-spin" /> : "✨"}
              AI suggests
            </span>
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                data-testid={`sms-suggestion-${i}`}
                onClick={() => setBody(s)}
                className="text-[12px] text-emerald-800 bg-white hover:bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 max-w-full truncate"
                title={s}
              >
                {s}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <button
            disabled
            className="w-9 h-9 shrink-0 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-200 disabled:opacity-50"
            title="Emoji (coming soon)"
          >
            <Smiley size={22} />
          </button>
          <button
            disabled
            className="w-9 h-9 shrink-0 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-200 disabled:opacity-50"
            title="Attachments (coming soon)"
          >
            <Paperclip size={20} />
          </button>
          <textarea
            data-testid="sms-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={personPhone ? `Message ${personName || personPhone}…` : "Type a message…"}
            rows={1}
            className="flex-1 resize-none rounded-lg px-4 py-2 text-[14.5px] focus:outline-none"
            style={{ background: "#ffffff", maxHeight: 120 }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
          />
          {body.trim() ? (
            <button
              onClick={onSend}
              disabled={sending}
              data-testid="sms-send-btn"
              className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full text-white disabled:opacity-50"
              style={{ background: "#00a884" }}
              title="Send"
            >
              {sending ? <Spinner size={16} className="animate-spin" /> : <PaperPlaneTilt size={20} weight="fill" />}
            </button>
          ) : (
            <button
              disabled
              className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-200 disabled:opacity-50"
              title="Voice (coming soon)"
            >
              <Microphone size={20} weight="fill" />
            </button>
          )}
        </div>
        <div className="mt-1 text-[10px] text-slate-500 px-1">
          {smsFrom ? <>Outbound from <span className="font-mono">{smsFrom}</span>.</> : "Outbound number not configured."}
          {" "}Press Enter to send · Shift+Enter for newline.
        </div>
      </div>
    </div>
  );
}

function DeliveryTicks({ status, failed }) {
  const s = (status || "").toLowerCase();
  if (failed) {
    return <span className="text-rose-600" title={status}>!</span>;
  }
  if (s === "read") {
    return <Checks size={14} weight="bold" className="text-sky-500" />;
  }
  if (s === "delivered" || s === "received") {
    return <Checks size={14} weight="bold" className="text-slate-500" />;
  }
  if (s === "sent") {
    return <Check size={14} weight="bold" className="text-slate-500" />;
  }
  // queued / sending
  return <Spinner size={11} className="animate-spin text-slate-400" />;
}

function formatBubbleTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function formatDayLabel(d) {
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(d, today)) return "Today";
  if (sameDay(d, yesterday)) return "Yesterday";
  const diff = (today - d) / (1000 * 60 * 60 * 24);
  if (diff < 7) return d.toLocaleDateString([], { weekday: "long" });
  return d.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
}

// Subtle WhatsApp-Web ribbon pattern (lightweight inline SVG, no extra requests).
const WA_PATTERN =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='110' height='110' viewBox='0 0 110 110'><g fill='%23d1c7b9' fill-opacity='0.35'><path d='M0 0h2v2H0zM10 8h2v2h-2zM22 18h2v2h-2zM34 6h2v2h-2zM48 22h2v2h-2zM62 4h2v2h-2zM78 16h2v2h-2zM92 8h2v2h-2zM6 32h2v2H6zM18 44h2v2h-2zM32 38h2v2h-2zM44 50h2v2h-2zM58 36h2v2h-2zM72 48h2v2h-2zM86 40h2v2h-2zM100 30h2v2h-2zM2 64h2v2H2zM14 76h2v2h-2zM26 68h2v2h-2zM40 80h2v2h-2zM54 70h2v2h-2zM68 84h2v2h-2zM82 72h2v2h-2zM96 86h2v2h-2zM10 96h2v2h-2zM24 102h2v2h-2zM38 92h2v2h-2zM52 104h2v2h-2zM66 94h2v2h-2zM80 100h2v2h-2zM94 102h2v2h-2z'/></g></svg>\")";
